export type AuthOperation = 'sign-in' | 'sign-up' | 'google' | 'password-reset';

interface AuthErrorLike {
  code?: unknown;
  message?: unknown;
}

const EMAIL_CREDENTIAL_ERROR_CODES = new Set([
  'auth/invalid-credential',
  'auth/rejected-credential',
  'auth/user-not-found',
  'auth/wrong-password',
]);

const CONFIGURATION_ERROR_CODES = new Set([
  'auth/app-deleted',
  'auth/app-not-authorized',
  'auth/auth-domain-config-required',
  'auth/invalid-api-key',
  'auth/invalid-app-id',
  'auth/invalid-oauth-client-id',
  'auth/invalid-oauth-provider',
  'auth/invalid-origin',
  'auth/invalid-tenant-id',
  'auth/tenant-id-mismatch',
  'auth/unauthorized-domain',
]);

const BROWSER_SUPPORT_ERROR_CODES = new Set([
  'auth/cors-unsupported',
  'auth/operation-not-supported-in-this-environment',
  'auth/unsupported-persistence-type',
  'auth/web-storage-unsupported',
]);

const MFA_ERROR_CODES = new Set([
  'auth/invalid-multi-factor-session',
  'auth/multi-factor-auth-required',
  'auth/multi-factor-info-not-found',
  'auth/missing-multi-factor-info',
  'auth/missing-multi-factor-session',
  'auth/unsupported-first-factor',
]);

const INTERNAL_ERROR_CODES = new Set([
  'auth/already-initialized',
  'auth/dependent-sdk-initialized-before-auth',
  'auth/emulator-config-failed',
  'auth/internal-error',
  'auth/invalid-auth-event',
  'auth/no-auth-event',
]);

function errorCode(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const code = (error as AuthErrorLike).code;
  return typeof code === 'string' ? code : '';
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (!error || typeof error !== 'object') return '';
  const message = (error as AuthErrorLike).message;
  return typeof message === 'string' ? message : '';
}

function isPasswordMismatch(error: unknown): boolean {
  return errorMessage(error).toLowerCase() === 'passwords do not match';
}

function credentialMessage(operation: AuthOperation): string {
  if (operation === 'google') {
    return 'Google did not accept the sign-in request. Try again.';
  }
  return 'The email or password is incorrect. Use your Workbench password or reset it below.';
}

/**
 * Converts Firebase Auth errors into safe messages for the login page.
 *
 * The fallback never exposes the SDK error text. Firebase can change that
 * text, and the raw text is not useful to a person who needs to sign in.
 */
export function friendlyAuthError(
  error: unknown,
  operation: AuthOperation = 'sign-in',
): string {
  if (isPasswordMismatch(error)) {
    return 'The passwords do not match. Enter the same password in both fields.';
  }

  const code = errorCode(error);

  if (operation === 'password-reset') {
    switch (code) {
      case 'auth/invalid-email':
      case 'auth/missing-email':
        return 'Enter a valid email address.';
      case 'auth/too-many-requests':
        return 'Too many reset requests. Wait a few minutes, then try again.';
      case 'auth/network-request-failed':
        return 'We could not send the reset email. Check your internet connection and try again.';
      default:
        return 'We could not send the reset email. Try again later.';
    }
  }

  if (EMAIL_CREDENTIAL_ERROR_CODES.has(code)) {
    return credentialMessage(operation);
  }

  switch (code) {
    case 'auth/argument-error':
      return 'Enter your email and password, then try again.';
    case 'auth/email-already-in-use':
      return 'An account already exists for this email. Switch to Sign In.';
    case 'auth/invalid-email':
      return 'Enter a valid email address.';
    case 'auth/missing-email':
      return 'Enter your email address.';
    case 'auth/missing-password':
      return 'Enter your password.';
    case 'auth/password-does-not-meet-requirements':
    case 'auth/weak-password':
      return operation === 'sign-up'
        ? 'Use a stronger password that meets the password requirements.'
        : 'The password is not accepted. Check it and try again.';
    case 'auth/user-disabled':
      return 'This account is disabled. Contact support for help.';
    case 'auth/too-many-requests':
      return 'Too many attempts were made. Wait a few minutes, then try again.';
    case 'auth/quota-exceeded':
      return 'The sign-in service reached its limit. Wait and try again later.';
    case 'auth/network-request-failed':
      return 'The sign-in service is not reachable. Check your internet connection and try again.';
    case 'auth/timeout':
      return 'The sign-in request timed out. Check your connection and try again.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not available. Contact support.';
    case 'auth/account-exists-with-different-credential':
    case 'auth/no-such-provider':
      return 'This account uses a different sign-in method. Sign in through Aligned with the method that you used to create the account.';
    case 'auth/credential-already-in-use':
      return 'This sign-in method belongs to another account. Use a different account.';
    case 'auth/provider-already-linked':
      return 'This sign-in method is already linked to the account.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the Google sign-in window. Allow pop-ups for this site and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/redirect-cancelled-by-user':
    case 'auth/user-cancelled':
      return 'Google sign-in was canceled. Try again.';
    case 'auth/redirect-operation-pending':
      return 'A Google sign-in request is already in progress. Finish it or reload the page.';
    case 'auth/user-token-expired':
    case 'auth/invalid-user-token':
      return 'Your sign-in session expired. Sign in again.';
    case 'auth/unverified-email':
      return 'Verify your email address before you continue. Check your inbox for the verification link.';
    case 'auth/unsupported-tenant-operation':
      return 'This sign-in method is not available for this account. Contact support.';
    default:
      break;
  }

  if (CONFIGURATION_ERROR_CODES.has(code)) {
    return 'This sign-in page is not configured correctly. Contact support.';
  }

  if (BROWSER_SUPPORT_ERROR_CODES.has(code)) {
    return 'This browser cannot store sign-in data. Enable cookies and site storage, then try again.';
  }

  if (MFA_ERROR_CODES.has(code)) {
    return 'This account needs an additional verification step. Finish sign-in through Aligned or contact support.';
  }

  if (INTERNAL_ERROR_CODES.has(code)) {
    return 'The sign-in service is not ready. Try again. If the error continues, contact support.';
  }

  if (operation === 'sign-up') {
    return 'We cannot create your account. Check your details and try again.';
  }
  if (operation === 'google') {
    return 'Google sign-in did not complete. Try again.';
  }
  return 'We cannot sign you in. Check your details and try again.';
}
